
import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function MuscleStrengthAssessment() {
  const [assessmentResults, setAssessmentResults] = useState({
    gripStrength: '',
    walkingSpeed: '',
    chairStand: '',
    calfCircumference: '',
    age: '',
    gender: 'male'
  });

  const [showResults, setShowResults] = useState(false);
  const [riskLevel, setRiskLevel] = useState('');

  const handleInputChange = (field: string, value: string) => {
    setAssessmentResults(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const calculateRisk = () => {
    const { gripStrength, walkingSpeed, chairStand, calfCircumference, age, gender } = assessmentResults;
    
    if (!gripStrength || !walkingSpeed || !chairStand || !calfCircumference || !age) {
      alert('すべての項目を入力してください');
      return;
    }

    let riskScore = 0;
    
    // 握力評価
    const grip = parseFloat(gripStrength);
    if (gender === 'male' && grip < 28) riskScore += 2;
    if (gender === 'female' && grip < 18) riskScore += 2;
    
    // 歩行速度評価
    const speed = parseFloat(walkingSpeed);
    if (speed < 1.0) riskScore += 2;
    
    // 椅子立ち上がりテスト
    const chairTime = parseFloat(chairStand);
    if (chairTime > 12) riskScore += 2;
    
    // ふくらはぎ周囲長
    const calf = parseFloat(calfCircumference);
    if (gender === 'male' && calf < 34) riskScore += 1;
    if (gender === 'female' && calf < 33) riskScore += 1;

    let risk = '';
    if (riskScore >= 4) {
      risk = 'high';
    } else if (riskScore >= 2) {
      risk = 'medium';
    } else {
      risk = 'low';
    }

    setRiskLevel(risk);
    setShowResults(true);
  };

  const getRiskMessage = () => {
    switch (riskLevel) {
      case 'high':
        return {
          title: '高リスク',
          message: 'サルコペニアのリスクが高い状態です。医療機関での詳しい検査と専門的な指導を受けることをお勧めします。',
          color: 'text-red-600 bg-red-50 border-red-200'
        };
      case 'medium':
        return {
          title: '中リスク',
          message: 'サルコペニアの予備軍の可能性があります。運動習慣の見直しとタンパク質摂取の改善を始めましょう。',
          color: 'text-yellow-600 bg-yellow-50 border-yellow-200'
        };
      case 'low':
        return {
          title: '低リスク',
          message: '現在の筋力は良好です。この状態を維持するため、継続的な運動とバランスの良い食事を心がけましょう。',
          color: 'text-green-600 bg-green-50 border-green-200'
        };
      default:
        return { title: '', message: '', color: '' };
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/" className="flex items-center">
                <div className="w-10 h-10 mr-3 relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-lg transform rotate-12 shadow-lg"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-emerald-400 rounded-lg flex items-center justify-center">
                    <i className="ri-heart-pulse-fill text-white text-lg"></i>
                  </div>
                </div>
                <div className="text-xl font-bold text-teal-600" style={{ fontFamily: '"Pacifico", serif' }}>
                  みんなの薬局～Pharmacare～
                </div>
              </Link>
            </div>
            <nav className="hidden md:flex space-x-8">
              <Link to="/health-support" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">健康サポート</Link>
              <Link to="/sarcopenia/strength-training-guidance" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">筋力トレーニング</Link>
              <Link to="/sarcopenia/protein-intake-guidance" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">タンパク質指導</Link>
              <Link to="/sarcopenia/progress-monitoring" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">進捗モニタリング</Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="bg-gray-50 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Link to="/" className="hover:text-teal-600 cursor-pointer">ホーム</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/health-support" className="hover:text-teal-600 cursor-pointer">健康サポート</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-gray-900">筋力測定・評価</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 to-indigo-50 py-16">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/8ae7946aacac00ed92abd2c5c01c6980.jpg"
            alt="筋力測定・評価"
            className="w-full h-full object-cover object-top opacity-20"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              筋力測定・評価
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              サルコペニア予防のための包括的な筋力評価を行います。握力、歩行速度、椅子立ち上がりテストなどの標準的な測定項目で現在の筋力状態を正確に把握しましょう。
            </p>
          </div>
        </div>
      </section>

      {/* Assessment Form Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">筋力評価テスト</h2>
              <p className="text-lg text-gray-600">以下の項目を測定して、サルコペニアのリスクを評価します</p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* 基本情報 */}
              <div className="space-y-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">基本情報</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">年齢</label>
                  <input
                    type="number"
                    value={assessmentResults.age}
                    onChange={(e) => handleInputChange('age', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="例: 65"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">性別</label>
                  <div className="flex space-x-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="gender"
                        value="male"
                        checked={assessmentResults.gender === 'male'}
                        onChange={(e) => handleInputChange('gender', e.target.value)}
                        className="mr-2"
                      />
                      男性
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="gender"
                        value="female"
                        checked={assessmentResults.gender === 'female'}
                        onChange={(e) => handleInputChange('gender', e.target.value)}
                        className="mr-2"
                      />
                      女性
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">握力（kg）</label>
                  <input
                    type="number"
                    step="0.1"
                    value={assessmentResults.gripStrength}
                    onChange={(e) => handleInputChange('gripStrength', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="例: 25.5"
                  />
                  <p className="text-xs text-gray-500 mt-1">握力計で測定した最大値を入力してください</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">歩行速度（m/秒）</label>
                  <input
                    type="number"
                    step="0.1"
                    value={assessmentResults.walkingSpeed}
                    onChange={(e) => handleInputChange('walkingSpeed', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="例: 1.2"
                  />
                  <p className="text-xs text-gray-500 mt-1">通常歩行での速度を入力してください</p>
                </div>
              </div>

              {/* 測定項目 */}
              <div className="space-y-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">身体測定</h3>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">椅子立ち上がりテスト（秒）</label>
                  <input
                    type="number"
                    step="0.1"
                    value={assessmentResults.chairStand}
                    onChange={(e) => handleInputChange('chairStand', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="例: 10.5"
                  />
                  <p className="text-xs text-gray-500 mt-1">椅子から5回立ち上がる時間を測定</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">ふくらはぎ周囲長（cm）</label>
                  <input
                    type="number"
                    step="0.1"
                    value={assessmentResults.calfCircumference}
                    onChange={(e) => handleInputChange('calfCircumference', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="例: 32.5"
                  />
                  <p className="text-xs text-gray-500 mt-1">ふくらはぎの最も太い部分を測定</p>
                </div>

                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-blue-900 mb-2">測定のポイント</h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• 握力：利き手で2回測定し、最大値を記録</li>
                    <li>• 歩行速度：4m歩行の時間を測定</li>
                    <li>• 椅子立ち上がり：腕を胸の前で組んで実施</li>
                    <li>• ふくらはぎ：立位で最も太い部分を測定</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="text-center mt-8">
              <button
                onClick={calculateRisk}
                className="bg-teal-600 text-white px-8 py-3 rounded-full font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                リスク評価を実行
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Results Section */}
      {showResults && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className={`rounded-2xl shadow-xl border p-8 ${getRiskMessage().color}`}>
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold mb-4">評価結果</h2>
                <div className="text-2xl font-bold mb-4">{getRiskMessage().title}</div>
                <p className="text-lg">{getRiskMessage().message}</p>
              </div>

              <div className="grid md:grid-cols-3 gap-6 mt-8">
                <div className="text-center">
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <i className="ri-fitness-line text-2xl text-teal-600"></i>
                  </div>
                  <h3 className="font-semibold mb-2">筋力トレーニング</h3>
                  <Link to="/sarcopenia/strength-training-guidance" className="text-teal-600 hover:text-teal-700 cursor-pointer">
                    トレーニング指導へ →
                  </Link>
                </div>

                <div className="text-center">
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <i className="ri-restaurant-line text-2xl text-teal-600"></i>
                  </div>
                  <h3 className="font-semibold mb-2">栄養指導</h3>
                  <Link to="/sarcopenia/protein-intake-guidance" className="text-teal-600 hover:text-teal-700 cursor-pointer">
                    タンパク質指導へ →
                  </Link>
                </div>

                <div className="text-center">
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <i className="ri-line-chart-line text-2xl text-teal-600"></i>
                  </div>
                  <h3 className="font-semibold mb-2">進捗管理</h3>
                  <Link to="/sarcopenia/progress-monitoring" className="text-teal-600 hover:text-teal-700 cursor-pointer">
                    モニタリングへ →
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Assessment Guidelines Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">評価基準について</h2>
            <p className="text-lg text-gray-600">各測定項目の基準値と評価方法をご説明します</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
              <div className="w-12 h-12 bg-red-50 text-red-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-hand-heart-line text-xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">握力</h3>
              <div className="space-y-2 text-sm text-gray-600">
                <p><strong>男性:</strong> 28kg未満でリスク</p>
                <p><strong>女性:</strong> 18kg未満でリスク</p>
                <p className="text-xs text-gray-500 mt-2">筋力の総合的な指標として重要</p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
              <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-walk-line text-xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">歩行速度</h3>
              <div className="space-y-2 text-sm text-gray-600">
                <p><strong>基準:</strong> 1.0m/秒未満でリスク</p>
                <p>日常生活動作能力の指標</p>
                <p className="text-xs text-gray-500 mt-2">転倒リスクとも関連</p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
              <div className="w-12 h-12 bg-green-50 text-green-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-armchair-line text-xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">椅子立ち上がり</h3>
              <div className="space-y-2 text-sm text-gray-600">
                <p><strong>基準:</strong> 12秒超でリスク</p>
                <p>下肢筋力の評価指標</p>
                <p className="text-xs text-gray-500 mt-2">5回連続立ち上がり時間</p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
              <div className="w-12 h-12 bg-purple-50 text-purple-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-ruler-line text-xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">ふくらはぎ周囲長</h3>
              <div className="space-y-2 text-sm text-gray-600">
                <p><strong>男性:</strong> 34cm未満でリスク</p>
                <p><strong>女性:</strong> 33cm未満でリスク</p>
                <p className="text-xs text-gray-500 mt-2">筋肉量の簡易評価指標</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Next Steps Section */}
      <section className="py-16 bg-gradient-to-br from-teal-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">次のステップ</h2>
            <p className="text-lg text-gray-600">評価結果に基づいて、適切な予防・改善プログラムを始めましょう</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Link to="/sarcopenia/strength-training-guidance" className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow p-8 border border-gray-100 cursor-pointer">
              <div className="w-16 h-16 bg-orange-50 text-orange-600 rounded-lg flex items-center justify-center mx-auto mb-6">
                <i className="ri-fitness-line text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4 text-center">筋力トレーニング指導</h3>
              <p className="text-gray-600 text-center mb-4">個人の体力レベルに合わせた安全で効果的なトレーニングプログラム</p>
              <div className="text-center">
                <span className="text-orange-600 font-medium">詳細を見る →</span>
              </div>
            </Link>

            <Link to="/sarcopenia/protein-intake-guidance" className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow p-8 border border-gray-100 cursor-pointer">
              <div className="w-16 h-16 bg-green-50 text-green-600 rounded-lg flex items-center justify-center mx-auto mb-6">
                <i className="ri-restaurant-line text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4 text-center">タンパク質摂取指導</h3>
              <p className="text-gray-600 text-center mb-4">筋肉合成に必要な栄養素の摂取方法とタイミングの指導</p>
              <div className="text-center">
                <span className="text-green-600 font-medium">詳細を見る →</span>
              </div>
            </Link>

            <Link to="/sarcopenia/progress-monitoring" className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow p-8 border border-gray-100 cursor-pointer">
              <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-lg flex items-center justify-center mx-auto mb-6">
                <i className="ri-line-chart-line text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4 text-center">進捗モニタリング</h3>
              <p className="text-gray-600 text-center mb-4">定期的な測定と評価による改善状況の追跡管理</p>
              <div className="text-center">
                <span className="text-blue-600 font-medium">詳細を見る →</span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 mr-2 relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-emerald-400 rounded-lg transform rotate-12"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-300 to-emerald-300 rounded-lg flex items-center justify-center">
                    <i className="ri-heart-pulse-fill text-white text-sm"></i>
                  </div>
                </div>
                <div className="text-xl font-bold text-teal-400" style={{ fontFamily: '"Pacifico", serif' }}>
                  みんなの薬局～Pharmacare～
                </div>
              </div>
              <p className="text-gray-400">
                患者さんと薬剤師をつなぐ総合医療情報プラットフォーム
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">サルコペニア予防</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/sarcopenia/muscle-strength-assessment" className="hover:text-white cursor-pointer">筋力測定・評価</Link></li>
                <li><Link to="/sarcopenia/strength-training-guidance" className="hover:text-white cursor-pointer">筋力トレーニング指導</Link></li>
                <li><Link to="/sarcopenia/protein-intake-guidance" className="hover:text-white cursor-pointer">タンパク質摂取指導</Link></li>
                <li><Link to="/sarcopenia/progress-monitoring" className="hover:text-white cursor-pointer">進捗モニタリング</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">健康サポート</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/health-support" className="hover:text-white cursor-pointer">セルフケア支援</Link></li>
                <li><Link to="/otc" className="hover:text-white cursor-pointer">OTC医薬品</Link></li>
                <li><Link to="/kampo" className="hover:text-white cursor-pointer">漢方相談</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">情報・リンク</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/information" className="hover:text-white cursor-pointer">公的機関リンク</Link></li>
                <li><Link to="/beast-blog" className="hover:text-white cursor-pointer">ビースト君ブログ</Link></li>
                <li><Link to="/career" className="hover:text-white cursor-pointer">転職・派遣情報</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 みんなの薬局～Pharmacare～. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-white cursor-pointer">Website Builder</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
